import { formatYmAsCrmMonthYear } from '../utils/dateTime';
import { fetchCollectionSalespersonCodes } from './portalUserProfiles';
import { getDbSupabase } from './supabase';

export type CollectionDueStatus =
  | 'overdue'
  | 'not_due'
  | 'paid'
  | 'credit'
  | 'waived'
  | 'applied'
  | 'reclass'
  | 'adjustment';
/** NAV Aged AR calendar-month buckets (plus legacy day-bucket aliases). */
export type CollectionAgingBucket =
  | 'not_due'
  | 'm0'
  | 'm1'
  | 'm2'
  | 'older'
  | 'current'
  | 'd1_30'
  | 'd31_60'
  | 'd61_90'
  | 'd90_plus';
export type CollectionCreditAlloc = 'invoice_link' | 'posting_date' | 'unallocated' | 'cm_waive' | '';

export type CollectionDocSortKey =
  | 'document_no'
  | 'outlet_code'
  | 'outlet_name'
  | 'document_type'
  | 'due_date'
  | 'days_past_due'
  | 'remaining_lcy'
  | 'aging_bucket'
  | 'due_status';

export interface AccountCollectionOutlet {
  code: string;
  name: string;
}

export interface AccountCollectionMonth {
  period_month: string;
  month_label: string;
  year_num: number;
  month_num: number;
  /** NAV Not Due bucket (due after period end). */
  is_not_due?: boolean;
  /** NAV aging bucket key: not_due | m0 | m1 | m2 | older */
  aging_bucket?: CollectionAgingBucket;
  outstanding_lcy: number;
  overdue_lcy: number;
  not_due_lcy: number;
  overdue_pct: number;
  not_due_pct: number;
  invoiced_lcy: number;
  collected_lcy: number;
  paid_lcy: number;
  /** Linked CM applied on closed invoices in this posting month. */
  waived_lcy: number;
  /** Cash + CM settled against invoiced. */
  settled_lcy: number;
  /** Cash collected after due date (excludes CM waive). */
  late_paid_lcy: number;
  unpaid_lcy: number;
  paid_pct: number;
  unpaid_pct: number;
  open_invoice_count: number;
  overdue_invoice_count: number;
}

export interface AccountCollectionDocument {
  outlet_code: string;
  outlet_name: string;
  entry_no: number;
  document_type: string;
  document_no: string;
  description: string;
  posting_date: string | null;
  posting_month: string | null;
  document_date: string | null;
  due_date: string | null;
  /** Due date used for month / aging match (linked CM follows INV). */
  period_due_date: string | null;
  closed_at_date: string | null;
  invoice_lcy: number;
  gross_remaining_lcy: number;
  credit_applied_lcy: number;
  remaining_lcy: number;
  collected_lcy: number;
  days_past_due: number;
  aging_bucket: CollectionAgingBucket;
  due_status: CollectionDueStatus;
  payment_terms_code: string;
  on_hold: string;
  external_document_no: string;
  linked_invoice_no: string;
  linked_cm_no: string;
  credit_alloc: CollectionCreditAlloc;
  ar_reference: string;
  payment_method_code: string;
  salesperson_code: string;
}

export interface AccountCollectionAging {
  not_due: number;
  m0: number;
  m1: number;
  m2: number;
  older: number;
  /** Legacy aliases (same values as NAV buckets). */
  current: number;
  d1_30: number;
  d31_60: number;
  d61_90: number;
  d90_plus: number;
  m0_label?: string;
  m1_label?: string;
  m2_label?: string;
  older_label?: string;
  period_end?: string;
  ledger_outstanding_lcy?: number;
  sync_gap_lcy?: number;
}

export interface AccountCollectionSummaryStats {
  outstanding_lcy: number;
  ledger_outstanding_lcy?: number;
  sync_gap_lcy?: number;
  overdue_lcy: number;
  not_due_lcy: number;
  not_due_today_lcy?: number;
  overdue_pct: number;
  not_due_pct: number;
  invoiced_lcy: number;
  paid_lcy: number;
  waived_lcy: number;
  settled_lcy: number;
  unpaid_lcy: number;
  paid_pct: number;
  unpaid_pct: number;
  open_invoice_count: number;
  overdue_invoice_count: number;
  credit_open_lcy: number;
  credit_applied_lcy: number;
  oldest_days_past_due: number;
  credit_limit_lcy: number | null;
  balance_lcy: number | null;
}

export interface AccountCollectionMeta {
  mother_code: string;
  today?: string;
  range_start?: string;
  period_end?: string;
}

export interface AccountCollectionSummary {
  outlets: AccountCollectionOutlet[];
  months: AccountCollectionMonth[];
  documents: AccountCollectionDocument[];
  aging: AccountCollectionAging;
  summary: AccountCollectionSummaryStats;
  meta: AccountCollectionMeta;
}

function num(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function str(value: unknown, fallback = ''): string {
  if (value == null) return fallback;
  return String(value);
}

function dateStr(value: unknown): string | null {
  if (value == null || value === '') return null;
  const s = String(value).slice(0, 10);
  if (s <= '1753-01-01') return null;
  return s;
}

function normalizeBucket(raw: string): CollectionAgingBucket {
  if (
    raw === 'not_due' ||
    raw === 'm0' ||
    raw === 'm1' ||
    raw === 'm2' ||
    raw === 'older' ||
    raw === 'd1_30' ||
    raw === 'd31_60' ||
    raw === 'd61_90' ||
    raw === 'd90_plus' ||
    raw === 'current'
  ) {
    return raw;
  }
  return 'not_due';
}

function normalizeDueStatus(raw: string): CollectionDueStatus {
  if (
    raw === 'overdue' ||
    raw === 'paid' ||
    raw === 'credit' ||
    raw === 'waived' ||
    raw === 'applied'
  ) {
    return raw;
  }
  return 'not_due';
}

function normalizeCreditAlloc(raw: string): CollectionCreditAlloc {
  if (raw === 'invoice_link' || raw === 'posting_date' || raw === 'unallocated' || raw === 'cm_waive') {
    return raw;
  }
  return '';
}

function normalizeDocument(raw: Record<string, unknown>): AccountCollectionDocument {
  return {
    outlet_code: str(raw.outlet_code),
    outlet_name: str(raw.outlet_name) || str(raw.outlet_code),
    entry_no: num(raw.entry_no),
    document_type: str(raw.document_type),
    document_no: str(raw.document_no),
    description: str(raw.description) || str(raw.document_no),
    posting_date: dateStr(raw.posting_date),
    posting_month: dateStr(raw.posting_month),
    document_date: dateStr(raw.document_date),
    due_date: dateStr(raw.due_date),
    period_due_date: dateStr(raw.period_due_date) || dateStr(raw.due_date),
    closed_at_date: dateStr(raw.closed_at_date),
    invoice_lcy: num(raw.invoice_lcy),
    gross_remaining_lcy: num(raw.gross_remaining_lcy, num(raw.remaining_lcy)),
    credit_applied_lcy: num(raw.credit_applied_lcy),
    remaining_lcy: num(raw.remaining_lcy),
    collected_lcy: num(raw.collected_lcy),
    days_past_due: Math.max(0, Math.round(num(raw.days_past_due))),
    aging_bucket: normalizeBucket(str(raw.aging_bucket, 'current')),
    due_status: normalizeDueStatus(str(raw.due_status, 'not_due')),
    payment_terms_code: str(raw.payment_terms_code),
    on_hold: str(raw.on_hold),
    external_document_no: str(raw.external_document_no),
    linked_invoice_no: str(raw.linked_invoice_no),
    linked_cm_no: str(raw.linked_cm_no),
    credit_alloc: normalizeCreditAlloc(str(raw.credit_alloc)),
    ar_reference: str(raw.ar_reference),
    payment_method_code: str(raw.payment_method_code),
    salesperson_code: str(raw.salesperson_code),
  };
}

function normalizeMonth(raw: Record<string, unknown>): AccountCollectionMonth {
  const bucketRaw = str(raw.aging_bucket);
  return {
    period_month: dateStr(raw.period_month) || str(raw.period_month),
    month_label: str(raw.month_label),
    year_num: Math.round(num(raw.year_num)),
    month_num: Math.round(num(raw.month_num)),
    is_not_due: Boolean(raw.is_not_due) || bucketRaw === 'not_due',
    aging_bucket: bucketRaw ? normalizeBucket(bucketRaw) : undefined,
    outstanding_lcy: num(raw.outstanding_lcy),
    overdue_lcy: num(raw.overdue_lcy),
    not_due_lcy: num(raw.not_due_lcy),
    overdue_pct: num(raw.overdue_pct),
    not_due_pct: num(raw.not_due_pct),
    invoiced_lcy: num(raw.invoiced_lcy),
    collected_lcy: num(raw.collected_lcy),
    paid_lcy: num(raw.paid_lcy, num(raw.collected_lcy)),
    waived_lcy: num(raw.waived_lcy),
    settled_lcy: num(
      raw.settled_lcy,
      num(raw.paid_lcy, num(raw.collected_lcy)) + num(raw.waived_lcy),
    ),
    late_paid_lcy: num(raw.late_paid_lcy),
    unpaid_lcy: num(raw.unpaid_lcy, num(raw.outstanding_lcy)),
    paid_pct: num(raw.paid_pct),
    unpaid_pct: num(raw.unpaid_pct),
    open_invoice_count: Math.round(num(raw.open_invoice_count)),
    overdue_invoice_count: Math.round(num(raw.overdue_invoice_count)),
  };
}

export function agingBucketLabel(
  bucket: CollectionAgingBucket,
  aging?: AccountCollectionAging | null,
): string {
  switch (bucket) {
    case 'not_due':
    case 'current':
      return 'Not Due';
    case 'm0':
    case 'd1_30':
      return aging?.m0_label || 'This month';
    case 'm1':
    case 'd31_60':
      return aging?.m1_label || 'Last month';
    case 'm2':
    case 'd61_90':
      return aging?.m2_label || '2 months ago';
    case 'older':
    case 'd90_plus':
      return aging?.older_label || 'Older';
    default:
      return 'Not Due';
  }
}

export type CollectionPaymentStatus = 'good' | 'slow' | 'bad_debt';

export interface CollectionPaymentStatusInfo {
  status: CollectionPaymentStatus;
  label: string;
  reason: string;
}

export type CollectionPaymentStatusInput = {
  balanceLcy?: number;
  overdueLcy: number;
  /** 0–1 ratio; derived from overdue/balance when omitted. */
  overduePct?: number;
  olderLcy?: number;
  avgPaidPct?: number;
  recentLatePaidLcy?: number;
  recentSettledLcy?: number;
  /** True when any posting month aged ≥2 months is still At risk (overdue open). */
  hasAgedAtRiskMonths?: boolean;
};

function formatStatusPct(ratio: number): string {
  return `${(100 * ratio).toFixed(0)}%`;
}

/**
 * Single source of truth for account payment status (list + detail).
 *
 * Potential Bad Debt:
 * - long-overdue open AR (aging.older / older_lcy) ≥ 8% of balance and ≥ SGD 500, OR
 * - overdue ≥ 55% of open balance, OR
 * - any posting month aged ≥ 2 months still At risk (overdue open) — matches month Movement
 *
 * Slow Payment:
 * - overdue ≥ 20% of open balance (but under Bad Debt), OR
 * - has overdue and recent paid% < 55, OR
 * - no overdue now but recent settlements are mostly late (≥35% late and ≥ SGD 500)
 *
 * Otherwise Good Payment.
 *
 * older_lcy must use the same basis as detail aging.older:
 * open invoice AR with posting month before "2 months ago".
 */
export function deriveCollectionPaymentStatus(
  input: CollectionPaymentStatusInput,
): CollectionPaymentStatusInfo {
  const balance = Math.max(input.balanceLcy ?? 0, 0);
  const overdue = Math.max(input.overdueLcy, 0);
  const overduePct = input.overduePct ?? (balance > 0.005 ? overdue / balance : 0);
  const older = Math.max(input.olderLcy ?? 0, 0);
  const olderPct = balance > 0.005 ? older / balance : 0;
  const avgPaidPct = input.avgPaidPct;
  const recentLatePaid = Math.max(0, input.recentLatePaidLcy ?? 0);
  const recentSettled = Math.max(0, input.recentSettledLcy ?? 0);
  const recentLatePaidPct = recentSettled > 0.005 ? recentLatePaid / recentSettled : 0;
  const hasOverdueBalance = overdue > 0.005;

  // Aged month Movement "At risk" (posted 2+ months ago, overdue still open)
  if (input.hasAgedAtRiskMonths) {
    return {
      status: 'bad_debt',
      label: 'Potential Bad Debt',
      reason: 'Aged months still At risk (overdue open)',
    };
  }

  // Long-overdue open balance (same signal as detail for One Paradise etc.)
  if (olderPct >= 0.08 && older >= 500) {
    return {
      status: 'bad_debt',
      label: 'Potential Bad Debt',
      reason: `${formatStatusPct(olderPct)} of balance posted 2+ months ago still open`,
    };
  }

  if (hasOverdueBalance && overduePct >= 0.55) {
    return {
      status: 'bad_debt',
      label: 'Potential Bad Debt',
      reason:
        avgPaidPct != null
          ? `Overdue ${formatStatusPct(overduePct)} · recent paid ${avgPaidPct.toFixed(0)}%`
          : `Overdue ${formatStatusPct(overduePct)} of balance`,
    };
  }

  if (!hasOverdueBalance) {
    if (recentLatePaid >= 500 && recentLatePaidPct >= 0.35) {
      return {
        status: 'slow',
        label: 'Slow Payment',
        reason: `No overdue now · late-paid ${formatStatusPct(recentLatePaidPct)} recently`,
      };
    }
    return {
      status: 'good',
      label: 'Good Payment',
      reason: 'No overdue balance',
    };
  }

  if (overduePct >= 0.2 || (avgPaidPct != null && avgPaidPct < 55)) {
    return {
      status: 'slow',
      label: 'Slow Payment',
      reason:
        avgPaidPct != null
          ? `Overdue ${formatStatusPct(overduePct)} · recent paid ${avgPaidPct.toFixed(0)}%`
          : `Overdue ${formatStatusPct(overduePct)} of balance`,
    };
  }

  return {
    status: 'good',
    label: 'Good Payment',
    reason: `Overdue ${formatStatusPct(overduePct)} of balance`,
  };
}

/** List Payment Status — same deriveCollectionPaymentStatus rules as detail. */
export function paymentStatusFromOverviewRow(
  row: AccountsCollectionOverviewRow,
): CollectionPaymentStatusInfo {
  const overdue = Math.max(row.overdue_lcy, 0);
  const notDue = Math.max(row.not_due_lcy, 0);
  const balance = Math.max(row.outstanding_lcy, overdue + notDue, 0);
  return deriveCollectionPaymentStatus({
    balanceLcy: balance,
    overdueLcy: row.overdue_lcy,
    overduePct: balance > 0.005 ? overdue / balance : row.overdue_pct / 100,
    olderLcy: row.older_lcy ?? 0,
    avgPaidPct: row.avg_paid_pct ?? undefined,
    recentLatePaidLcy: row.recent_late_paid_lcy ?? 0,
    recentSettledLcy: row.recent_settled_lcy ?? 0,
    hasAgedAtRiskMonths: row.has_aged_at_risk === true,
  });
}

/** Exact same payment status as Collection detail page. */
export async function fetchDetailPaymentStatus(
  motherCode: string,
): Promise<CollectionPaymentStatusInfo> {
  const metrics = await fetchDetailCollectionListMetrics(motherCode);
  return metrics.payment;
}

/** Detail-page KPIs for one mother (same fetch + reconcile as Collection detail). */
export type CollectionListDetailMetrics = {
  payment: CollectionPaymentStatusInfo;
  outstanding_lcy: number;
  overdue_lcy: number;
  not_due_lcy: number;
  overdue_pct: number;
  open_invoice_count: number;
  overdue_invoice_count: number;
  older_lcy: number;
};

export async function fetchDetailCollectionListMetrics(
  motherCode: string,
): Promise<CollectionListDetailMetrics> {
  const detail = await fetchAccountCollectionSummary(motherCode);
  const payment = derivePaymentStatus(detail.summary, detail.aging, detail.months);
  const outstanding = Math.max(detail.summary.outstanding_lcy, detail.summary.unpaid_lcy, 0);
  const overdue = Math.max(detail.summary.overdue_lcy, 0);
  const notDue = Math.max(detail.summary.not_due_lcy, 0);
  const balance = Math.max(outstanding, overdue + notDue, 0);
  return {
    payment,
    outstanding_lcy: roundMoney2(balance),
    overdue_lcy: roundMoney2(overdue),
    not_due_lcy: roundMoney2(notDue),
    overdue_pct:
      balance > 0.005 ? Math.round((1000 * overdue) / balance) / 10 : 0,
    open_invoice_count: detail.summary.open_invoice_count,
    overdue_invoice_count: detail.summary.overdue_invoice_count,
    older_lcy: Math.max(detail.aging.older ?? detail.aging.d90_plus ?? 0, 0),
  };
}

/** Overlay detail metrics onto an overview row (list = detail algorithm). */
export function applyDetailMetricsToOverviewRow(
  row: AccountsCollectionOverviewRow,
  metrics: CollectionListDetailMetrics,
): AccountsCollectionOverviewRow {
  return {
    ...row,
    outstanding_lcy: metrics.outstanding_lcy,
    overdue_lcy: metrics.overdue_lcy,
    not_due_lcy: metrics.not_due_lcy,
    overdue_pct: metrics.overdue_pct,
    open_invoice_count: metrics.open_invoice_count,
    overdue_invoice_count: metrics.overdue_invoice_count,
    older_lcy: metrics.older_lcy,
  };
}

/** @deprecated Use paymentStatusFromOverviewRow or deriveCollectionPaymentStatus. */
export function classifyCollectionPaymentFromOverview(
  overduePct: number,
  _oldestDaysPastDue: number,
  overdueLcy = 0,
): CollectionPaymentStatusInfo {
  const ratio = overduePct > 1 ? overduePct / 100 : overduePct;
  return deriveCollectionPaymentStatus({
    balanceLcy: overdueLcy > 0.005 && ratio > 0.005 ? overdueLcy / ratio : Math.abs(overdueLcy),
    overdueLcy,
    overduePct: ratio,
  });
}

/** Account-level payment movement from aging + recent monthly paid %. */
export function derivePaymentStatus(
  summary: AccountCollectionSummaryStats,
  aging: AccountCollectionAging,
  months: AccountCollectionMonth[],
): CollectionPaymentStatusInfo {
  const balance = Math.max(summary.outstanding_lcy, summary.unpaid_lcy, 0);
  const overdue = Math.max(summary.overdue_lcy, 0);
  const older = Math.max(aging.older ?? aging.d90_plus ?? 0, 0);

  const recent = [...months]
    .filter((m) => m.invoiced_lcy > 0)
    .sort((a, b) => b.period_month.localeCompare(a.period_month))
    .slice(0, 3);
  const avgPaidPct =
    recent.length > 0
      ? recent.reduce((s, m) => s + (monthPaidPct(m) ?? m.paid_pct ?? 0), 0) / recent.length
      : summary.invoiced_lcy > 0.005 && summary.settled_lcy > 0.005
        ? Math.min(100, (100 * summary.settled_lcy) / summary.invoiced_lcy)
        : summary.paid_pct;
  const recentLatePaid = recent.reduce((s, m) => s + Math.max(0, m.late_paid_lcy || 0), 0);
  const recentSettled = recent.reduce((s, m) => s + Math.max(0, monthSettledLcy(m)), 0);

  // Align top Payment Status with month Movement: aged At risk months escalate.
  const hasAgedAtRiskMonths = months.some((m) => {
    if (m.unpaid_lcy <= 0.005 || m.overdue_lcy <= 0.005) return false;
    if (monthAgeFromPeriod(m.period_month) < 2) return false;
    return deriveMonthPaymentMovement(m).status === 'bad_debt';
  });

  return deriveCollectionPaymentStatus({
    balanceLcy: balance,
    overdueLcy: summary.overdue_lcy,
    overduePct: balance > 0.005 ? overdue / balance : 0,
    olderLcy: older,
    avgPaidPct,
    recentLatePaidLcy: recentLatePaid,
    recentSettledLcy: recentSettled,
    hasAgedAtRiskMonths,
  });
}

/** Calendar months between posting period and current month (0 = this month). */
function monthAgeFromPeriod(periodMonth: string, today = new Date()): number {
  const raw = (periodMonth || '').slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(raw)) return 0;
  const [y, m] = raw.split('-').map((v) => Number(v));
  if (!y || !m) return 0;
  return (today.getFullYear() - y) * 12 + (today.getMonth() + 1 - m);
}

/** Per-month movement: overdue open, or paid late after due date. */
export function deriveMonthPaymentMovement(
  month: AccountCollectionMonth,
  today = new Date(),
): CollectionPaymentStatusInfo {
  if (month.invoiced_lcy <= 0 && month.unpaid_lcy <= 0) {
    return { status: 'good', label: '—', reason: 'No activity' };
  }

  const hasOverdueOpen = month.overdue_lcy > 0.005;
  const hasLatePaid = (month.late_paid_lcy || 0) > 0.005;
  const paidPct = monthPaidPct(month) ?? month.paid_pct;
  const ageMonths = monthAgeFromPeriod(month.period_month, today);
  const overdueShareOfUnpaid =
    month.unpaid_lcy > 0.005 ? Math.max(0, month.overdue_lcy) / month.unpaid_lcy : 0;

  // Still open and within terms
  if (!hasOverdueOpen && month.unpaid_lcy > 0.005) {
    return { status: 'good', label: 'Current', reason: 'Open but not overdue' };
  }

  // Fully settled, but cash came after due date — label as paid late, not open "Slow"
  if (!hasOverdueOpen && month.unpaid_lcy <= 0.005 && hasLatePaid) {
    return { status: 'slow', label: 'Paid late', reason: 'Paid after due date' };
  }

  if (!hasOverdueOpen && month.unpaid_lcy <= 0.005) {
    return { status: 'good', label: 'Good', reason: 'Paid on time' };
  }

  // Still has overdue open — aged months with remaining overdue are at risk,
  // even if earlier invoices in that month were mostly paid.
  if (ageMonths >= 2 && overdueShareOfUnpaid >= 0.5) {
    return {
      status: 'bad_debt',
      label: 'At risk',
      reason: `Overdue still open · posted ${ageMonths} months ago`,
    };
  }
  if (paidPct < 40) {
    return {
      status: 'bad_debt',
      label: 'At risk',
      reason: 'Overdue open with low paid %',
    };
  }
  return { status: 'slow', label: 'Slow', reason: 'Has overdue open' };
}

/** Cash + linked CM settled on invoices in the posting month. */
export function monthSettledLcy(month: AccountCollectionMonth): number {
  if (month.settled_lcy > 0.005) return month.settled_lcy;
  return (month.paid_lcy || month.collected_lcy || 0) + (month.waived_lcy || 0);
}

/** Paid % = Paid / (Paid + Unpaid) as shown on the month row. */
export function monthPaidPct(month: AccountCollectionMonth): number | null {
  const paid = Math.max(0, month.paid_lcy || month.collected_lcy || 0);
  const unpaid = Math.max(0, month.unpaid_lcy || 0);
  const split = paid + unpaid;
  if (split > 0.005) return Math.min(100, (100 * paid) / split);
  if (month.invoiced_lcy <= 0.005 && unpaid <= 0.005 && paid <= 0.005) return null;
  return unpaid <= 0.005 ? 100 : 0;
}

/** Unpaid % = Unpaid / (Paid + Unpaid) as shown on the month row. */
export function monthUnpaidPct(month: AccountCollectionMonth): number | null {
  const paid = Math.max(0, month.paid_lcy || month.collected_lcy || 0);
  const unpaid = Math.max(0, month.unpaid_lcy || 0);
  const split = paid + unpaid;
  if (split > 0.005) return Math.min(100, (100 * unpaid) / split);
  if (month.invoiced_lcy <= 0.005 && unpaid <= 0.005 && paid <= 0.005) return null;
  return unpaid <= 0.005 ? 0 : 100;
}

/** Of paid amount, % collected after due date (0–100). */
export function monthLateOfPaidPct(month: AccountCollectionMonth): number | null {
  if (month.paid_lcy <= 0.005) return null;
  return (100 * month.late_paid_lcy) / month.paid_lcy;
}

/**
 * Overdue recovery: of late-paid + still-overdue open, % that was paid (late).
 * Answers “overdue but still paid”.
 */
export function monthOverdueRecoveryPct(month: AccountCollectionMonth): number | null {
  const pool = month.late_paid_lcy + month.overdue_lcy;
  if (pool <= 0.005) return null;
  return (100 * month.late_paid_lcy) / pool;
}

export function formatCollectionPct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}%`;
}

export function matchesCollectionSearch(doc: AccountCollectionDocument, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    doc.document_no.toLowerCase().includes(q) ||
    doc.description.toLowerCase().includes(q) ||
    doc.outlet_code.toLowerCase().includes(q) ||
    doc.outlet_name.toLowerCase().includes(q) ||
    doc.external_document_no.toLowerCase().includes(q) ||
    doc.ar_reference.toLowerCase().includes(q) ||
    doc.document_type.toLowerCase().includes(q)
  );
}

/** Effective posting month for month-row matching (linked CM/SRN → invoice posting month). */
export function docEffectivePostingMonth(doc: AccountCollectionDocument): string | null {
  return doc.period_due_date || doc.posting_month || doc.posting_date;
}

/** Match document to a posting-month row (NAV Aged by Posting Date). */
export function docMatchesDueMonth(
  doc: AccountCollectionDocument,
  month: AccountCollectionMonth,
  _periodEnd?: string | null,
): boolean {
  const period = docEffectivePostingMonth(doc);
  if (!period || !month.period_month) return false;
  return period.slice(0, 7) === month.period_month.slice(0, 7);
}

function roundMoney2(value: number): number {
  return Math.round(value * 100) / 100;
}

function isInvoiceLikeType(type: string): boolean {
  const t = type.trim().toUpperCase();
  return t === 'INVOICE' || t === 'FINANCE CHARGE MEMO';
}

function isCreditMemoType(type: string): boolean {
  return type.trim().toUpperCase() === 'CREDIT MEMO';
}

function isOpenInvoiceLikeDoc(doc: AccountCollectionDocument): boolean {
  return isInvoiceLikeType(doc.document_type) && doc.remaining_lcy > 0.005;
}

function docIsOverdueByDueDate(doc: AccountCollectionDocument): boolean {
  if (doc.due_status === 'overdue') return true;
  if (doc.due_status === 'not_due') return false;
  return doc.days_past_due > 0;
}

function docNoKey(value: string): string {
  return (value || '').trim().toUpperCase();
}

/** Invoice amount after linked CM (what Collection should show). */
export function docAmountNetOfCm(doc: AccountCollectionDocument): number {
  if (isCreditMemoType(doc.document_type)) {
    if (doc.due_status === 'applied' || doc.due_status === 'waived') return 0;
    return doc.remaining_lcy;
  }
  if (!isInvoiceLikeType(doc.document_type)) return doc.remaining_lcy;

  const cm = Math.max(0, doc.credit_applied_lcy || 0);
  if (
    doc.due_status === 'paid' ||
    doc.due_status === 'waived' ||
    doc.due_status === 'applied'
  ) {
    return roundMoney2(Math.max(0, (doc.invoice_lcy || 0) - cm));
  }
  // Open lines are already CM-netted into remaining_lcy.
  return roundMoney2(doc.remaining_lcy);
}

/** CM applied against previously-open invoice lines (not already in month.waived_lcy). */
function openNettedCmForMonth(
  documents: AccountCollectionDocument[],
  month: AccountCollectionMonth,
  periodEnd?: string | null,
): number {
  let sum = 0;
  for (const doc of documents) {
    if (!isInvoiceLikeType(doc.document_type)) continue;
    if (!docMatchesDueMonth(doc, month, periodEnd)) continue;
    // open_lines_display keeps gross_remaining; paid_docs use 0 — so this is open-CM only.
    if ((doc.gross_remaining_lcy || 0) <= 0.005) continue;
    sum += Math.max(0, doc.credit_applied_lcy || 0);
  }
  return roundMoney2(sum);
}

function invoicedNetOfCm(invoiced: number, waived: number, openNettedCm = 0): number {
  return roundMoney2(Math.max(0, invoiced - Math.max(0, waived) - Math.max(0, openNettedCm)));
}

/**
 * Display docs keep NAV raw open amounts (INV + linked CM both visible).
 * For KPIs / status, deduct open CM linked to an invoice (same mother account).
 */
export function netLinkedOpenCredits(
  documents: AccountCollectionDocument[],
): AccountCollectionDocument[] {
  type CmPool = { entryNo: number; left: number };
  const pools = new Map<string, CmPool[]>();

  for (const doc of documents) {
    if (!isCreditMemoType(doc.document_type)) continue;
    const invNo = docNoKey(doc.linked_invoice_no);
    if (!invNo.startsWith('INV-')) continue;
    if (doc.due_status === 'applied' || doc.due_status === 'waived') continue;
    const amt = Math.abs(doc.remaining_lcy);
    if (amt <= 0.005) continue;
    const list = pools.get(invNo) || [];
    list.push({ entryNo: doc.entry_no, left: amt });
    pools.set(invNo, list);
  }
  if (pools.size === 0) return documents;

  const cmApplied = new Map<number, number>(); // entry_no → applied abs
  const next = documents.map((doc) => {
    if (!isInvoiceLikeType(doc.document_type)) return doc;
    if (doc.remaining_lcy <= 0.005) return doc;

    const invNo = docNoKey(doc.document_no);
    const pool = pools.get(invNo);
    if (!pool || pool.length === 0) return doc;

    let need = doc.remaining_lcy;
    let applied = 0;
    for (const cm of pool) {
      if (need <= 0.005) break;
      if (cm.left <= 0.005) continue;
      const take = Math.min(need, cm.left);
      cm.left = roundMoney2(cm.left - take);
      need = roundMoney2(need - take);
      applied = roundMoney2(applied + take);
      cmApplied.set(cm.entryNo, roundMoney2((cmApplied.get(cm.entryNo) || 0) + take));
    }
    if (applied <= 0.005) return doc;

    const remaining = roundMoney2(doc.remaining_lcy - applied);
    if (remaining <= 0.005) {
      const creditApplied = roundMoney2((doc.credit_applied_lcy || 0) + applied);
      const cashCollected =
        (doc.collected_lcy || 0) > 0.005
          ? doc.collected_lcy
          : roundMoney2(Math.max(0, (doc.invoice_lcy || 0) - applied));
      // CM only cleared the open remainder — not a full invoice write-off.
      const fullCmWriteOff =
        (doc.invoice_lcy || 0) <= applied + 0.02 && cashCollected <= 0.005;
      return {
        ...doc,
        credit_applied_lcy: creditApplied,
        remaining_lcy: 0,
        collected_lcy: cashCollected,
        due_status: (fullCmWriteOff ? 'waived' : 'paid') as CollectionDueStatus,
        credit_alloc: 'invoice_link' as CollectionCreditAlloc,
        days_past_due: 0,
      };
    }
    return {
      ...doc,
      credit_applied_lcy: roundMoney2((doc.credit_applied_lcy || 0) + applied),
      remaining_lcy: remaining,
      credit_alloc: 'invoice_link' as CollectionCreditAlloc,
    };
  });

  return next.map((doc) => {
    const applied = cmApplied.get(doc.entry_no) || 0;
    if (applied <= 0.005 || !isCreditMemoType(doc.document_type)) return doc;
    const gross = Math.abs(doc.remaining_lcy);
    const left = roundMoney2(gross - applied);
    if (left <= 0.005) {
      return {
        ...doc,
        credit_applied_lcy: roundMoney2((doc.credit_applied_lcy || 0) + applied),
        remaining_lcy: 0,
        due_status: 'applied' as CollectionDueStatus,
        credit_alloc: 'invoice_link' as CollectionCreditAlloc,
        days_past_due: 0,
      };
    }
    return {
      ...doc,
      credit_applied_lcy: roundMoney2((doc.credit_applied_lcy || 0) + applied),
      remaining_lcy: doc.remaining_lcy < 0 ? -left : left,
      credit_alloc: 'invoice_link' as CollectionCreditAlloc,
    };
  });
}

/**
 * Align Collection with NAV Aged AR:
 * - Balance = ledger_outstanding (SUM open CLE Remaining) — Apply Entries already in Remaining.
 * - Month Unpaid = backend posting-month CLE bucket (CM in its posting month). Do not invent applies.
 * - Overdue/Not due = due-date split of open invoices, scaled to that month unpaid.
 * - Document rows may still net linked-but-unapplied CM for display only.
 *
 * detailed_customer_ledger is useful to *explain* applications; not required for Balance accuracy.
 */
export function reconcileCollectionOverdueByDueDate(
  data: AccountCollectionSummary,
): AccountCollectionSummary {
  const documents = netLinkedOpenCredits(data.documents);
  const openDocs = documents.filter(isOpenInvoiceLikeDoc);
  const periodEnd = data.meta.period_end;

  let creditOpen = 0;
  let openNettedCmTotal = 0;
  for (const doc of data.documents) {
    if (!isCreditMemoType(doc.document_type)) continue;
    if (doc.due_status === 'applied' || doc.due_status === 'waived') continue;
    if (Math.abs(doc.remaining_lcy) > 0.005) creditOpen += Math.abs(doc.remaining_lcy);
  }
  for (const doc of documents) {
    if (
      isInvoiceLikeType(doc.document_type) &&
      (doc.gross_remaining_lcy || 0) > 0.005 &&
      (doc.credit_applied_lcy || 0) > 0.005
    ) {
      openNettedCmTotal += doc.credit_applied_lcy;
    }
  }
  creditOpen = roundMoney2(creditOpen);

  const months = data.months.map((month) => {
    // NAV posting-month bucket from CLE (includes open CM posted in that month).
    const navUnpaid = roundMoney2(month.unpaid_lcy ?? month.outstanding_lcy);

    const docs = openDocs.filter((d) => docMatchesDueMonth(d, month, periodEnd));
    let invOverdue = 0;
    let invNotDue = 0;
    for (const doc of docs) {
      if (docIsOverdueByDueDate(doc)) invOverdue += doc.remaining_lcy;
      else invNotDue += doc.remaining_lcy;
    }
    invOverdue = roundMoney2(invOverdue);
    invNotDue = roundMoney2(invNotDue);
    const invOpen = roundMoney2(invOverdue + invNotDue);

    let overdue = 0;
    let notDue = 0;
    if (Math.abs(navUnpaid) <= 0.005) {
      overdue = 0;
      notDue = 0;
    } else if (invOpen <= 0.005 || navUnpaid < 0) {
      overdue = 0;
      notDue = navUnpaid;
    } else {
      overdue = roundMoney2(navUnpaid * (invOverdue / invOpen));
      notDue = roundMoney2(navUnpaid - overdue);
    }

    const unpaid = roundMoney2(overdue + notDue);
    const openCm = openNettedCmForMonth(documents, month, periodEnd);
    const invoiced = invoicedNetOfCm(month.invoiced_lcy, month.waived_lcy, openCm);
    const paid = roundMoney2(Math.max(0, invoiced - Math.max(0, unpaid)));
    const split = paid + Math.max(0, unpaid);

    return {
      ...month,
      overdue_lcy: Math.max(0, overdue),
      not_due_lcy: notDue,
      unpaid_lcy: unpaid,
      outstanding_lcy: unpaid,
      invoiced_lcy: invoiced,
      paid_lcy: paid,
      collected_lcy: paid,
      settled_lcy: paid,
      paid_pct:
        split > 0.005
          ? Math.min(100, roundMoney2((100 * paid) / split))
          : Math.max(0, unpaid) <= 0.005
            ? 100
            : 0,
      unpaid_pct:
        split > 0.005
          ? Math.min(100, roundMoney2((100 * Math.max(0, unpaid)) / split))
          : Math.max(0, unpaid) <= 0.005
            ? 0
            : 100,
    };
  });

  let totalOverdue = 0;
  let totalNotDue = 0;
  for (const m of months) {
    totalOverdue += Math.max(0, m.overdue_lcy);
    totalNotDue += m.not_due_lcy;
  }
  totalOverdue = roundMoney2(totalOverdue);
  totalNotDue = roundMoney2(totalNotDue);
  const monthsNet = roundMoney2(totalOverdue + totalNotDue);

  // Prefer CLE open sum (same basis as NAV Aged AR total).
  const ledgerNet = data.summary.ledger_outstanding_lcy;
  const customerBal = data.summary.balance_lcy;
  const hasLedger = ledgerNet != null && Number.isFinite(ledgerNet);
  const hasCustomerBal = customerBal != null && Number.isFinite(customerBal);
  let balance = monthsNet;
  if (hasLedger) {
    balance = roundMoney2(ledgerNet as number);
  } else if (hasCustomerBal) {
    balance = roundMoney2(customerBal as number);
  }

  if (Math.abs(balance) <= 0.005) {
    totalOverdue = 0;
    totalNotDue = 0;
  } else if (Math.abs(monthsNet) > 0.005 && Math.abs(monthsNet - balance) > 0.02) {
    const scale = balance / monthsNet;
    totalOverdue = roundMoney2(Math.max(0, totalOverdue * scale));
    totalNotDue = roundMoney2(balance - totalOverdue);
  } else if (Math.abs(monthsNet) <= 0.005) {
    totalNotDue = balance;
    totalOverdue = 0;
  }

  const overduePct =
    Math.abs(balance) > 0.005
      ? Math.round((1000 * Math.max(0, totalOverdue)) / Math.abs(balance)) / 10
      : 0;

  const invoicedNet = invoicedNetOfCm(
    data.summary.invoiced_lcy,
    data.summary.waived_lcy,
    openNettedCmTotal,
  );
  const paidNet = roundMoney2(Math.max(0, invoicedNet - Math.max(0, balance)));
  const summarySplit = paidNet + Math.max(0, balance);

  return {
    ...data,
    documents,
    months,
    summary: {
      ...data.summary,
      overdue_lcy: Math.max(0, totalOverdue),
      not_due_lcy: totalNotDue,
      outstanding_lcy: balance,
      unpaid_lcy: balance,
      ledger_outstanding_lcy: hasLedger ? (ledgerNet as number) : balance,
      overdue_pct: overduePct,
      not_due_pct:
        Math.abs(balance) > 0.005
          ? Math.round((1000 * Math.max(0, totalNotDue)) / Math.abs(balance)) / 10
          : 0,
      open_invoice_count: openDocs.filter((d) => d.remaining_lcy > 0.005).length,
      overdue_invoice_count: openDocs.filter(
        (d) => d.remaining_lcy > 0.005 && docIsOverdueByDueDate(d),
      ).length,
      credit_open_lcy: creditOpen,
      invoiced_lcy: invoicedNet,
      paid_lcy: paidNet,
      settled_lcy: paidNet,
      paid_pct:
        summarySplit > 0.005
          ? Math.min(100, roundMoney2((100 * paidNet) / summarySplit))
          : Math.max(0, balance) <= 0.005
            ? 100
            : 0,
      unpaid_pct:
        summarySplit > 0.005
          ? Math.min(100, roundMoney2((100 * Math.max(0, balance)) / summarySplit))
          : Math.max(0, balance) <= 0.005
            ? 0
            : 100,
    },
  };
}

export function sortCollectionDocs(
  rows: AccountCollectionDocument[],
  key: CollectionDocSortKey,
  dir: 'asc' | 'desc',
): AccountCollectionDocument[] {
  const mul = dir === 'asc' ? 1 : -1;
  const bucketRank: Record<CollectionAgingBucket, number> = {
    not_due: 0,
    current: 0,
    m0: 1,
    d1_30: 1,
    m1: 2,
    d31_60: 2,
    m2: 3,
    d61_90: 3,
    older: 4,
    d90_plus: 4,
  };
  return [...rows].sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'document_no':
      case 'outlet_code':
      case 'outlet_name':
      case 'document_type':
        cmp = (a[key] || '').localeCompare(b[key] || '', undefined, { sensitivity: 'base' });
        break;
      case 'due_date':
        cmp = (a.due_date || '').localeCompare(b.due_date || '');
        break;
      case 'days_past_due':
      case 'remaining_lcy':
        cmp = a[key] - b[key];
        break;
      case 'aging_bucket':
        cmp = bucketRank[a.aging_bucket] - bucketRank[b.aging_bucket];
        break;
      case 'due_status': {
        const rank: Record<CollectionDueStatus, number> = {
          overdue: 0,
          not_due: 1,
          waived: 2,
          applied: 3,
          reclass: 4,
          adjustment: 5,
          paid: 6,
          credit: 7,
        };
        cmp = rank[a.due_status] - rank[b.due_status];
        break;
      }
      default:
        cmp = 0;
    }
    if (cmp !== 0) return cmp * mul;
    return a.document_no.localeCompare(b.document_no);
  });
}

export interface AccountsCollectionOverviewRow {
  mother_code: string;
  mother_name: string;
  salesperson_code?: string;
  outlet_count: number;
  outstanding_lcy: number;
  overdue_lcy: number;
  not_due_lcy: number;
  /** Posting-month buckets (NAV aged-by-posting-date). */
  m0_lcy: number;
  m1_lcy: number;
  m2_lcy: number;
  m3_lcy: number;
  before_lcy: number;
  /** Due-date overdue within each posting-month bucket (list highlight). */
  m0_overdue_lcy?: number;
  m1_overdue_lcy?: number;
  m2_overdue_lcy?: number;
  m3_overdue_lcy?: number;
  before_overdue_lcy?: number;
  /** Open AR posted before m2 (same basis as detail aging.older) — payment status. */
  older_lcy?: number;
  overdue_pct: number;
  open_invoice_count: number;
  overdue_invoice_count: number;
  /** @deprecated Not used for payment status; kept if API still returns it. */
  oldest_days_past_due?: number;
  /** Same signals as detail derivePaymentStatus (from overview RPC). */
  has_aged_at_risk?: boolean;
  avg_paid_pct?: number | null;
  recent_late_paid_lcy?: number;
  recent_settled_lcy?: number;
}

export interface CollectionAgingLabels {
  m0: string;
  m1: string;
  m2: string;
  m3: string;
  before: string;
}

/** Short headers like 08/26 (posting-month buckets). */
export function buildCollectionAgingLabels(
  todayIso: string | null | undefined,
): CollectionAgingLabels {
  const parsed = todayIso ? new Date(`${todayIso.trim()}T12:00:00`) : new Date();
  const d = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const monthLabel = (monthOffset: number) => {
    const x = new Date(d.getFullYear(), d.getMonth() + monthOffset, 1);
    const mm = String(x.getMonth() + 1).padStart(2, '0');
    const yy = String(x.getFullYear()).slice(-2);
    return `${mm}/${yy}`;
  };
  return {
    m0: monthLabel(0),
    m1: monthLabel(-1),
    m2: monthLabel(-2),
    m3: monthLabel(-3),
    before: `Before ${monthLabel(-3)}`,
  };
}

export type OverviewAgingBucket = 'm0' | 'm1' | 'm2' | 'm3' | 'before';

function parseTodayDate(todayIso: string | null | undefined): Date {
  const parsed = todayIso ? new Date(`${todayIso.trim()}T12:00:00`) : new Date();
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

/** YYYY-MM for m0–m3; null for the folded "before" bucket. */
export function overviewAgingPeriodMonth(
  bucket: OverviewAgingBucket,
  todayIso?: string | null,
): string | null {
  if (bucket === 'before') return null;
  const d = parseTodayDate(todayIso);
  const offset = bucket === 'm0' ? 0 : bucket === 'm1' ? -1 : bucket === 'm2' ? -2 : -3;
  const x = new Date(d.getFullYear(), d.getMonth() + offset, 1);
  const mm = String(x.getMonth() + 1).padStart(2, '0');
  return `${x.getFullYear()}-${mm}`;
}

/** Long title like "08/2026" for document modal. */
export function overviewAgingMonthTitle(
  bucket: OverviewAgingBucket,
  todayIso?: string | null,
  months?: AccountCollectionMonth[],
): string {
  if (bucket === 'before') {
    const m3 = overviewAgingPeriodMonth('m3', todayIso);
    if (m3) {
      return `Before ${formatYmAsCrmMonthYear(m3)}`;
    }
    return 'Before';
  }
  const ym = overviewAgingPeriodMonth(bucket, todayIso);
  if (ym && months?.length) {
    const found = months.find((row) => (row.period_month || '').slice(0, 7) === ym);
    if (found?.month_label) return found.month_label;
  }
  if (!ym) return 'Documents';
  return formatYmAsCrmMonthYear(ym);
}

export function docMatchesOverviewAgingBucket(
  doc: AccountCollectionDocument,
  bucket: OverviewAgingBucket,
  todayIso?: string | null,
): boolean {
  const period = docEffectivePostingMonth(doc)?.slice(0, 7);
  if (!period) return false;
  if (bucket === 'before') {
    const m3 = overviewAgingPeriodMonth('m3', todayIso);
    return !!m3 && period < m3;
  }
  const target = overviewAgingPeriodMonth(bucket, todayIso);
  return !!target && period === target;
}

export interface AccountsCollectionOverview {
  accounts: AccountsCollectionOverviewRow[];
  summary: {
    account_count: number;
    outstanding_lcy: number;
    overdue_lcy: number;
    not_due_lcy: number;
    m0_lcy: number;
    m1_lcy: number;
    m2_lcy: number;
    m3_lcy: number;
    before_lcy: number;
    overdue_pct: number;
    open_invoice_count: number;
    overdue_invoice_count: number;
  };
  meta: {
    today: string | null;
    scope: string;
    division?: string;
    salesperson_scope?: string;
    salesperson_codes?: string[];
    aging_labels?: CollectionAgingLabels;
  };
}

export function normalizeSalespersonCode(code: string): string {
  return code
    .trim()
    // Normalize common dash / minus variants from Excel / NAV exports.
    .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
    .replace(/\s+/g, '')
    .toUpperCase();
}

/** Canonical display/storage form: `SA2 - RL` (spaces around hyphen). `SA5` stays `SA5`. */
export function formatSalespersonCode(code: string): string {
  const raw = code
    .trim()
    .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-');
  if (!raw) return '';
  const match = raw.match(/^([A-Za-z]+\d+)\s*-\s*(.+)$/);
  if (match) {
    const head = match[1].toUpperCase();
    const tail = match[2].replace(/\s+/g, ' ').trim().toUpperCase();
    return `${head} - ${tail}`;
  }
  return raw.replace(/\s+/g, ' ').toUpperCase();
}

export function rowMatchesSalespersonCodes(
  row: AccountsCollectionOverviewRow,
  codes: string[],
): boolean {
  if (codes.length === 0) return true;
  const rowNorm = normalizeSalespersonCode(row.salesperson_code ?? '');
  if (!rowNorm) return false;
  return codes.some((code) => normalizeSalespersonCode(code) === rowNorm);
}

export function summarizeCollectionAccounts(
  accounts: AccountsCollectionOverviewRow[],
): AccountsCollectionOverview['summary'] {
  const outstanding_lcy = accounts.reduce((sum, row) => sum + row.outstanding_lcy, 0);
  const overdue_lcy = accounts.reduce((sum, row) => sum + row.overdue_lcy, 0);
  const not_due_lcy = accounts.reduce((sum, row) => sum + row.not_due_lcy, 0);
  const m0_lcy = accounts.reduce((sum, row) => sum + row.m0_lcy, 0);
  const m1_lcy = accounts.reduce((sum, row) => sum + row.m1_lcy, 0);
  const m2_lcy = accounts.reduce((sum, row) => sum + row.m2_lcy, 0);
  const m3_lcy = accounts.reduce((sum, row) => sum + row.m3_lcy, 0);
  const before_lcy = accounts.reduce((sum, row) => sum + row.before_lcy, 0);
  const open_invoice_count = accounts.reduce((sum, row) => sum + row.open_invoice_count, 0);
  const overdue_invoice_count = accounts.reduce(
    (sum, row) => sum + row.overdue_invoice_count,
    0,
  );
  const overdue_pct =
    Math.abs(outstanding_lcy) < 0.005 ? 0 : (100 * overdue_lcy) / Math.abs(outstanding_lcy);
  return {
    account_count: accounts.length,
    outstanding_lcy,
    overdue_lcy,
    not_due_lcy,
    m0_lcy,
    m1_lcy,
    m2_lcy,
    m3_lcy,
    before_lcy,
    overdue_pct: Math.round(overdue_pct * 10) / 10,
    open_invoice_count,
    overdue_invoice_count,
  };
}

export function applySalespersonScopeToOverview(
  overview: AccountsCollectionOverview,
  codes: string[],
): AccountsCollectionOverview {
  if (codes.length === 0) return overview;
  const accounts = overview.accounts.filter((row) => rowMatchesSalespersonCodes(row, codes));
  return {
    ...overview,
    accounts,
    summary: summarizeCollectionAccounts(accounts),
    meta: {
      ...overview.meta,
      salesperson_scope: 'filtered',
      salesperson_codes: codes,
    },
  };
}

/** NAV global_dimension_1_code — BTH sales division for Accounts Collection list. */
export const ACCOUNTS_COLLECTION_DIVISION = '10';

export async function fetchAccountsCollectionOverview(
  division: string = ACCOUNTS_COLLECTION_DIVISION,
): Promise<AccountsCollectionOverview> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const ownCodes = await fetchCollectionSalespersonCodes().catch(() => [] as string[]);
  try {
    const { data } = await getDbSupabase('crm').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy rejects unauthenticated
  }

  const response = await fetch(
    `/account-sales-api?resource=collection_overview&division=${encodeURIComponent(division)}`,
    { headers },
  );
  const json = (await response.json().catch(() => null)) as
    | (Record<string, unknown> & { error?: string; detail?: string; hint?: string })
    | null;

  if (!response.ok) {
    const detail =
      typeof json?.detail === 'string' && json.detail.trim() ? ` (${json.detail.trim()})` : '';
    const hint =
      typeof json?.hint === 'string' && json.hint.trim() ? ` ${json.hint.trim()}` : '';
    throw new Error(
      (json?.error || `Failed to load collection overview (${response.status}).`) + detail + hint,
    );
  }
  if (!json || typeof json !== 'object') {
    throw new Error('Invalid collection overview response.');
  }

  const accountsRaw = Array.isArray(json.accounts) ? json.accounts : [];
  const summaryRaw =
    json.summary && typeof json.summary === 'object'
      ? (json.summary as Record<string, unknown>)
      : {};
  const metaRaw =
    json.meta && typeof json.meta === 'object' ? (json.meta as Record<string, unknown>) : {};

  const overview: AccountsCollectionOverview = {
    accounts: accountsRaw
      .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
      .map((row) => ({
        mother_code: str(row.mother_code),
        mother_name: str(row.mother_name) || str(row.mother_code),
        salesperson_code: str(row.salesperson_code) || undefined,
        outlet_count: num(row.outlet_count),
        outstanding_lcy: num(row.outstanding_lcy),
        overdue_lcy: num(row.overdue_lcy),
        not_due_lcy: num(row.not_due_lcy),
        m0_lcy: num(row.m0_lcy),
        m1_lcy: num(row.m1_lcy),
        m2_lcy: num(row.m2_lcy),
        m3_lcy: num(row.m3_lcy),
        before_lcy: num(row.before_lcy),
        m0_overdue_lcy: num(row.m0_overdue_lcy),
        m1_overdue_lcy: num(row.m1_overdue_lcy),
        m2_overdue_lcy: num(row.m2_overdue_lcy),
        m3_overdue_lcy: num(row.m3_overdue_lcy),
        before_overdue_lcy: num(row.before_overdue_lcy),
        older_lcy: num(row.older_lcy),
        overdue_pct: num(row.overdue_pct),
        open_invoice_count: num(row.open_invoice_count),
        overdue_invoice_count: num(row.overdue_invoice_count),
        oldest_days_past_due: row.oldest_days_past_due != null ? num(row.oldest_days_past_due) : undefined,
        has_aged_at_risk: row.has_aged_at_risk === true,
        avg_paid_pct:
          row.avg_paid_pct == null || row.avg_paid_pct === ''
            ? null
            : num(row.avg_paid_pct),
        recent_late_paid_lcy: num(row.recent_late_paid_lcy),
        recent_settled_lcy: num(row.recent_settled_lcy),
      })),
    summary: {
      account_count: num(summaryRaw.account_count),
      outstanding_lcy: num(summaryRaw.outstanding_lcy),
      overdue_lcy: num(summaryRaw.overdue_lcy),
      not_due_lcy: num(summaryRaw.not_due_lcy),
      m0_lcy: num(summaryRaw.m0_lcy),
      m1_lcy: num(summaryRaw.m1_lcy),
      m2_lcy: num(summaryRaw.m2_lcy),
      m3_lcy: num(summaryRaw.m3_lcy),
      before_lcy: num(summaryRaw.before_lcy),
      overdue_pct: num(summaryRaw.overdue_pct),
      open_invoice_count: num(summaryRaw.open_invoice_count),
      overdue_invoice_count: num(summaryRaw.overdue_invoice_count),
    },
    meta: {
      today: str(metaRaw.today) || null,
      scope: str(metaRaw.scope, `division_${division}`),
      division: str(metaRaw.division, division) || division,
      salesperson_scope:
        typeof metaRaw.salesperson_scope === 'string' ? metaRaw.salesperson_scope : undefined,
      salesperson_codes: Array.isArray(metaRaw.salesperson_codes)
        ? metaRaw.salesperson_codes.map((code) => String(code ?? '').trim()).filter(Boolean)
        : undefined,
      aging_labels: buildCollectionAgingLabels(str(metaRaw.today) || null),
    },
  };

  if (ownCodes.length > 0 && overview.meta.salesperson_scope !== 'filtered') {
    return applySalespersonScopeToOverview(overview, ownCodes);
  }
  return overview;
}

export async function fetchAccountCollectionSummary(
  motherCode: string,
): Promise<AccountCollectionSummary> {
  const code = motherCode.trim();
  if (!code) {
    throw new Error('Account code is required.');
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  try {
    const { data } = await getDbSupabase('crm').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy will reject unauthenticated
  }

  const response = await fetch(
    `/account-sales-api?mother_code=${encodeURIComponent(code)}&resource=collection`,
    { headers },
  );
  const json = (await response.json().catch(() => null)) as
    | (Record<string, unknown> & { error?: string; detail?: string; hint?: string })
    | null;

  if (!response.ok) {
    const detail =
      typeof json?.detail === 'string' && json.detail.trim() ? ` (${json.detail.trim()})` : '';
    const hint =
      typeof json?.hint === 'string' && json.hint.trim() ? ` ${json.hint.trim()}` : '';
    throw new Error(
      (json?.error || `Failed to load collection (${response.status}).`) + detail + hint,
    );
  }
  if (!json || typeof json !== 'object') {
    throw new Error('Invalid collection response.');
  }

  const outletsRaw = Array.isArray(json.outlets) ? json.outlets : [];
  const monthsRaw = Array.isArray(json.months) ? json.months : [];
  const docsRaw = Array.isArray(json.documents) ? json.documents : [];
  const agingRaw =
    json.aging && typeof json.aging === 'object' ? (json.aging as Record<string, unknown>) : {};
  const summaryRaw =
    json.summary && typeof json.summary === 'object'
      ? (json.summary as Record<string, unknown>)
      : {};
  const metaRaw =
    json.meta && typeof json.meta === 'object' ? (json.meta as Record<string, unknown>) : {};

  // Unpaid stays posting-month (NAV). Overdue/Not due re-split by due date.
  return reconcileCollectionOverdueByDueDate({
    outlets: outletsRaw
      .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
      .map((o) => ({
        code: str(o.code),
        name: str(o.name) || str(o.code),
      })),
    months: monthsRaw
      .filter((m): m is Record<string, unknown> => !!m && typeof m === 'object')
      .map(normalizeMonth),
    documents: docsRaw
      .filter((d): d is Record<string, unknown> => !!d && typeof d === 'object')
      .map(normalizeDocument),
    aging: {
      not_due: num(agingRaw.not_due, num(agingRaw.current)),
      m0: num(agingRaw.m0, num(agingRaw.d1_30)),
      m1: num(agingRaw.m1, num(agingRaw.d31_60)),
      m2: num(agingRaw.m2, num(agingRaw.d61_90)),
      older: num(agingRaw.older, num(agingRaw.d90_plus)),
      current: num(agingRaw.current, num(agingRaw.not_due)),
      d1_30: num(agingRaw.d1_30, num(agingRaw.m0)),
      d31_60: num(agingRaw.d31_60, num(agingRaw.m1)),
      d61_90: num(agingRaw.d61_90, num(agingRaw.m2)),
      d90_plus: num(agingRaw.d90_plus, num(agingRaw.older)),
      m0_label: str(agingRaw.m0_label) || undefined,
      m1_label: str(agingRaw.m1_label) || undefined,
      m2_label: str(agingRaw.m2_label) || undefined,
      older_label: str(agingRaw.older_label) || undefined,
      period_end: dateStr(agingRaw.period_end) ?? undefined,
      ledger_outstanding_lcy: num(agingRaw.ledger_outstanding_lcy),
      sync_gap_lcy: num(agingRaw.sync_gap_lcy),
    },
    summary: {
      outstanding_lcy: num(summaryRaw.outstanding_lcy),
      ledger_outstanding_lcy: num(summaryRaw.ledger_outstanding_lcy),
      sync_gap_lcy: num(summaryRaw.sync_gap_lcy),
      overdue_lcy: num(summaryRaw.overdue_lcy),
      not_due_lcy: num(summaryRaw.not_due_lcy),
      not_due_today_lcy: num(summaryRaw.not_due_today_lcy),
      overdue_pct: num(summaryRaw.overdue_pct),
      not_due_pct: num(summaryRaw.not_due_pct),
      invoiced_lcy: num(summaryRaw.invoiced_lcy),
      paid_lcy: num(summaryRaw.paid_lcy),
      waived_lcy: num(summaryRaw.waived_lcy),
      settled_lcy: num(
        summaryRaw.settled_lcy,
        num(summaryRaw.paid_lcy) + num(summaryRaw.waived_lcy),
      ),
      unpaid_lcy: num(summaryRaw.unpaid_lcy, num(summaryRaw.outstanding_lcy)),
      paid_pct: num(summaryRaw.paid_pct),
      unpaid_pct: num(summaryRaw.unpaid_pct),
      open_invoice_count: Math.round(num(summaryRaw.open_invoice_count)),
      overdue_invoice_count: Math.round(num(summaryRaw.overdue_invoice_count)),
      credit_open_lcy: num(summaryRaw.credit_open_lcy),
      credit_applied_lcy: num(summaryRaw.credit_applied_lcy),
      oldest_days_past_due: Math.round(num(summaryRaw.oldest_days_past_due)),
      credit_limit_lcy:
        summaryRaw.credit_limit_lcy == null || summaryRaw.credit_limit_lcy === ''
          ? null
          : num(summaryRaw.credit_limit_lcy),
      balance_lcy:
        summaryRaw.balance_lcy == null || summaryRaw.balance_lcy === ''
          ? null
          : num(summaryRaw.balance_lcy),
    },
    meta: {
      mother_code: str(metaRaw.mother_code, code),
      today: dateStr(metaRaw.today) ?? undefined,
      range_start: dateStr(metaRaw.range_start) ?? undefined,
      period_end: dateStr(metaRaw.period_end) ?? undefined,
    },
  });
}
